import device from "current-device";
import {
  UPDATE_SERVICE_WORKER_UPDATE_STATUS,
  UPDATE_LATEST_VERSION,
  UPDATE_WIDE_LAYOUT,
  SET_PAGE_TITLE,
  UPDATE_TOUCH_DEVICE,
  UPDATE_APP_CONNECTION_STATUS,
  UPDATE_LAST_CONNECTION_FAIL_TIME,
  ORIENTATION_CHANGED,
  UPDATE_VIEWPORT_WIDTH,
  DEVICE_READY,
  SET_AD_BLOCKER_DETECTED,
  MARK_AD_BLOCKER_WARNING_SHOWN
} from './actions.js';

import Bowser from "bowser";
import merge from 'lodash-es/merge.js';

/**
 * @returns {Object} common/default configration for all enviroments.
 */
const getDefaultConfig = () => {
  return {
    attachment: {
      file: {
        maxSizeInMb: 200
      }
    },
    auth: {
      baseUrl: 'https://auth.kerika-test2.dreamworld.solutions',
      googleClientId: '837399046096-qu9s9rm843o0fhvuqosr5kb0qqncmg3o.apps.googleusercontent.com',
      loginApiUrl: '/authcallback/{service}?api-login&code={authCode}',
      additionalAuthApiUrl: '/user/api/link-auth?service={service}&code={authCode}',
      userUIBaseUrl: 'https://auth-user.kerika-test2.dreamworld.solutions'
    },
    appLink: {
      ios: 'https://kerika-test2.dreamworld.solutions',
      android: 'https://kerika-test2.dreamworld.solutions'
    },
    bgColors: ["#E8E4DD", "#E9F0FF", "#F1FFE9", "#FFE9F1", "#D5D8DC", "#DBC0DA", "#F7CDDB", "#8CA3C9", "#C2B1C5", "#D8E3DF", "#D8E1E3", "#E1E3D8", "#D8DCE3", "#C0C7C8", "#BCCBCE"],
    iframelyApiKey: 'a7cde2e1e1bb57df0e542e',
    //In minutes
    undoCardTimeout: 3,
    viewAutoRefreshTimeoutInMinutes: 20,
    lastSeenPushIntervalInSeconds: 30,
    lastSeenPushIntervalInSecondsForFreshUser: 10,
    //In Days
    fcmTokenRefreshTime: 30,
    redirectEndpoint: '/util/r?url={url}&referrer={referrer}&lang={lang}&context={context}',
    trialExpiryNotificationBeforeDays: 7,
    autoRenewOffNotificationBeforeDays: 7,
    nonProfitCoupon: "non-profit-discount",
    /**
     * Mobile pull-to-refresh gesture knobs — consumed by
     * [pull-to-refresh-mixin](../../components/pull-to-refresh/pull-to-refresh-mixin.js).
     * Each key has a hardcoded fallback in the mixin if absent here. Firebase
     * config can override any of these at runtime without a code release.
     */
    pullToRefresh: {
      // Multiplier applied to raw finger displacement (shownPx = rawPx * dragResistance).
      // Smaller = stickier pull. Range: 0..1.
      dragResistance: 0.5,
      // Vertical pull distance (after drag resistance) at which release commits a reload.
      activationThresholdPx: 80,
      // Maximum pull travel — indicator stops following finger past this.
      maxPullPx: 120,
      // shownPx at which the icon reaches full opacity. Smaller = icon visible faster.
      opacityFullPx: 20,
      // After crossing activationThresholdPx, reverse-swiping shownPx by this many
      // pixels from the peak cancels the gesture (no reload on release).
      cancelReverseSwipePx: 80,
      // Delay between committing the gesture and calling `window.location.reload()`.
      // Trades off visible refreshing state vs. perceived stall.
      refreshDelayMs: 400,
    },
    aiChat: {
      recencyThresoldHours: 24,
      defaultPreview: 'ai-chat-default-tool-preview',
      /**
       * Tool-specific configuration.
       * Each tool can have:
       * - component: String - Preview component name (optional, uses defaultPreview if not specified)
       * - status: Array<String> - List of statuses at which tool preview should be shown.
       *   Possible values: 'PENDING', 'WAITING_FOR_APPROVAL', 'IN_PROGRESS', 'SUCCESS', 'FAILED',
       *   'REJECTED', 'CANCELLED'
       * - leafTools: Array<String> - For virtual grouping entries only. The selector finds all
       *   ToolCalls whose name is in this list and groups them by input[groupBy].
       * - groupBy: String - Field on ToolCall.input used to group leafTools (e.g. 'cardId').
       * - keepVisible: Boolean - Keeps the tool's row outside the folded "Worked for…" summary
       *   once the turn ends, where the edit proposals go, instead of folding it away with the
       *   reads. For results the user needs to see that are not proposals (no approval).
       *
       * Tool preview is shown only when tool's current status is in the configured status array.
       */
      tools: {
        // Reads. They reach the trail through their terminal states, so a finished step
        // stays on screen instead of vanishing the moment it completes. No `component`:
        // `_renderPreview` falls through to `defaultPreview`, which is the row we want.
        //
        // WAITING_FOR_APPROVAL is absent by design — a read is never approval-gated, and
        // listing it would put reads on the approval path.
        ...Object.fromEntries([
          'getBoard', 'getCard', 'findBoards', 'findCards', 'findHighlightedCards', 'search',
          'getUnread', 'getActivities', 'getChats', 'getAttachments',
          'readFileAttachmentContent', 'getAccessibleAccounts', 'webSearch', 'webFetch',
          'readCard', 'readSubtask', 'readFile', 'readTag', 'readBoard', 'readUser',
        ].map((name) => [name, { status: ['PENDING', 'IN_PROGRESS', 'SUCCESS', 'FAILED'] }])),

        // Plumbing. Listed with no visible status so the filter drops them: they say
        // nothing a user would call work. `searchUsers` turns a name into a `usr_` id so a
        // team edit can run, and `getBoardTagColors` reads the palette so a tag edit can
        // name a colour by id — each is the silent half of the edit that follows, and that
        // edit is what the user sees. The rule is that the server should not send any of
        // these at all; until it does, this is where that lives.
        ...Object.fromEntries([
          'generateEntityId', 'resolveLink', 'checkPendingBoardEdits', 'loadSkill',
          'searchUsers', 'getBoardTagColors',
        ].map((name) => [name, { status: [] }])),

        readAccessibleAccounts: {
          component: 'ai-chat-default-tool-preview',
          status: ['IN_PROGRESS']
        },
        createNewBoard: {
          component: 'ai-chat-create-board-tool-preview',
          status: ['IN_PROGRESS', 'SUCCESS'],
          keepVisible: true
        },
        cloneBoard: {
          component: 'ai-chat-create-board-tool-preview',
          status: ['IN_PROGRESS', 'SUCCESS'],
          keepVisible: true
        },

        // AI Edit tools
        createCard: {
          component: 'ai-chat-create-card-tool-call',
          status: ['WAITING_FOR_APPROVAL', 'IN_PROGRESS', 'SUCCESS', 'FAILED', 'REJECTED', 'CANCELLED']
        },
        editCard: {
          component: 'ai-chat-edit-card-tool-call',
          status: ['WAITING_FOR_APPROVAL', 'IN_PROGRESS', 'SUCCESS', 'FAILED', 'REJECTED', 'CANCELLED'],
          groupBy: 'cardId',
          leafTools: [
            'updateCardTitle', 'updateCardDescription', 'updateCardDueDate', 'updateCardPriority',
            'updateCardStatus', 'updateCardTags', 'updateCardAssignments', 'moveCard',
            'createSubtask', 'updateSubtaskTitle', 'updateSubtaskAssignments', 'updateSubtaskDueDate',
            'updateSubtaskOrder', 'updateSubtaskDone', 'deleteSubtask',
            'createAttachment', 'updateAttachmentTitle', 'updateAttachmentUrl', 'updateAttachmentContent',
            'deleteAttachment', 'createChatMessage', 'updateChatMessage', 'deleteChatMessage',
            'updateBoard'
          ]
        },
        deleteCard: {
          component: 'ai-chat-delete-card-tool-call',
          status: ['WAITING_FOR_APPROVAL', 'IN_PROGRESS', 'SUCCESS', 'FAILED', 'REJECTED', 'CANCELLED']
        },
        // Board-level edit surfaces — each a virtual grouping entry (`leafTools` + `groupBy:
        // 'boardId'`), so `visibleTools` synthesises one row per board, the way `editCard`
        // does per card. Same status set as the card edit tools. Which area each row opens is
        // owned by its own `ai-chat-board-*-tool-call` component (its `_openArea`), not config.
        boardChats: {
          component: 'ai-chat-board-chat-tool-call',
          status: ['WAITING_FOR_APPROVAL', 'IN_PROGRESS', 'SUCCESS', 'FAILED', 'REJECTED', 'CANCELLED'],
          groupBy: 'boardId',
          leafTools: ['createBoardChatMessage', 'updateBoardChatMessage', 'deleteBoardChatMessage']
        },
        boardAttachments: {
          component: 'ai-chat-board-attachment-tool-call',
          status: ['WAITING_FOR_APPROVAL', 'IN_PROGRESS', 'SUCCESS', 'FAILED', 'REJECTED', 'CANCELLED'],
          groupBy: 'boardId',
          leafTools: ['createBoardAttachment', 'updateBoardAttachmentTitle', 'deleteBoardAttachment']
        },
        boardTeam: {
          component: 'ai-chat-board-team-tool-call',
          status: ['WAITING_FOR_APPROVAL', 'IN_PROGRESS', 'SUCCESS', 'FAILED', 'REJECTED', 'CANCELLED'],
          groupBy: 'boardId',
          leafTools: ['addBoardTeamMember', 'updateBoardTeamMemberRole', 'removeBoardTeamMember']
        },
        boardColumns: {
          component: 'ai-chat-board-column-tool-call',
          status: ['WAITING_FOR_APPROVAL', 'IN_PROGRESS', 'SUCCESS', 'FAILED', 'REJECTED', 'CANCELLED'],
          groupBy: 'boardId',
          leafTools: ['createColumn', 'updateColumn', 'deleteColumn']
        },
        boardTags: {
          component: 'ai-chat-board-tag-tool-call',
          status: ['WAITING_FOR_APPROVAL', 'IN_PROGRESS', 'SUCCESS', 'FAILED', 'REJECTED', 'CANCELLED'],
          groupBy: 'boardId',
          leafTools: ['createBoardTag', 'updateBoardTag', 'deleteBoardTag', 'mergeBoardTags']
        },
        boardSettings: {
          component: 'ai-chat-board-setting-tool-call',
          status: ['WAITING_FOR_APPROVAL', 'IN_PROGRESS', 'SUCCESS', 'FAILED', 'REJECTED', 'CANCELLED'],
          groupBy: 'boardId',
          leafTools: ['updateBoardName', 'updateBoardDescription', 'updateBoardPrivacy',
                      'updateBoardWipLimitEnabled', 'updateBoardAutoNumbering',
                      'updateBoardTagsEnabled', 'updateBoardInViewSettings']
        }
      },
      /**
       * List of file extensions that are not supported for AI chat attachments.
       * Files with these extensions are blocked client-side before upload —
       * excluded from the file picker via the `accept` attribute and rejected on drag-and-drop.
       */
      notSupportedFiles: [
        // Image formats not supported
        '.gif',
        // Archive formats
        '.zip', '.rar', '.7z', '.tar', '.gz', '.bz2', '.xz',
        // Executable / system binaries
        '.exe', '.dll', '.bin', '.iso', '.dmg', '.msi', '.deb', '.rpm',
        // Database / data files
        '.dat', '.db', '.sqlite', '.bak',
        // Font files
        '.ttf', '.otf', '.woff', '.woff2',
        // Video formats
        '.mp4', '.m4v', '.mkv', '.avi', '.mov', '.wmv', '.flv', '.webm',
        '.mpeg', '.mpg', '.3gp', '.3g2', '.ogv', '.ogg', '.rm', '.rmvb',
        '.asf', '.vob', '.ts', '.mts', '.m2ts', '.f4v', '.divx', '.xvid',
        '.dv', '.h264', '.h265', '.hevc', '.avchd', '.mxf', '.roq', '.nsv', '.yuv',
        // Audio formats
        '.mp3', '.wav', '.aac', '.ogg', '.flac', '.m4a', '.wma', '.aiff', '.aif',
        '.opus', '.amr', '.alac', '.mid', '.midi', '.ra', '.rm', '.caf', '.dsd',
        '.dts', '.ac3', '.au', '.voc', '.8svx',
      ],
      maxFileSizeInMb: 2
    }
  }
}

/**
 * @returns {Object} enviroments configration.
 */
const getConfig = () => {
  let config = window.K.config || {};
  let authCongfig = {
    baseUrl: window.K.config.authConfig_baseURL ,
    googleClientId: window.K.config.authConfig_googleClientId,
    loginApiUrl: '/authcallback/{service}?api-login&code={authCode}',
    userUIBaseUrl: window.K.config.authConfig_userUIBaseURL
  };

  let appLink = {
    ios: window.K.config.appLink_ios,
    android: window.K.config.appLink_android
  }

  const bgColors = window.K.config.bgColors && K.config.bgColors.split(',') || [];

  const serviceWorker = { latestVersionDocumentPath: "app/app-pwa", latestVersionField: "latestVersion" }

  return merge({}, config, {auth: authCongfig, bgColors, appLink, serviceWorker});
}

// Set device OS, browser, platform information from bowser library.
const bowser = Bowser.parse(window.navigator.userAgent);

const INITIAL_STATE = {
  config: merge({}, getDefaultConfig(), getConfig()),
  version: {
    current: window.K.config.version,
    latest: undefined,
    updateStatus: undefined
  },
  pageTitle: 'Kerika',
  touchDevice: false,
  bowser: bowser,
  orientation: device.orientation,
  viewportWidth: window.innerWidth || 0,
  deviceReady: false
};

const app = (state = INITIAL_STATE, action) => {
  switch (action.type) {
    case UPDATE_SERVICE_WORKER_UPDATE_STATUS:
      return {
        ...state,
        version: {
          ...state.version,
          updateStatus: action.value
        }
      };

    case UPDATE_LATEST_VERSION:
      return {
        ...state,
        version: {
          ...state.version,
          latest: action.latestVersion
        }
      }

    case UPDATE_WIDE_LAYOUT:
      return {
        ...state,
        wideLayout: action.wide
      };

    case UPDATE_TOUCH_DEVICE:
      return {
        ...state,
        touchDevice: action.value
      }

    case SET_PAGE_TITLE: {
      return {
        ...state,
        pageTitle: action.title
      }
    }

    case UPDATE_APP_CONNECTION_STATUS:
      return {
        ...state,
        online: action.status
      }

    case UPDATE_LAST_CONNECTION_FAIL_TIME:
      return {
        ...state,
        lastConnectionFailTime: action.value
      }

    case ORIENTATION_CHANGED:
    return {
      ...state,
      orientation: action.orientation
    }
    case UPDATE_VIEWPORT_WIDTH:
      return {
        ...state,
        viewportWidth: action.width
      }
    case DEVICE_READY:
      return { ...state, deviceReady: true }
    default:
      return state;
  }
};

/**
 * Ad blocker detection + warning state. Lives in its own root slice rather than under `app`
 * because `lastShownDate` is persisted: persistEnhancer preloads persisted paths before the
 * reducers are registered, so a preloaded slice never gets its initial state applied. That is
 * harmless here (nothing to lose) but would wipe `app`'s INITIAL_STATE.
 */
export const adBlockerReducer = (state = {}, action) => {
  switch (action.type) {
    case SET_AD_BLOCKER_DETECTED:
      return { ...state, detected: action.detected, source: action.source }

    case MARK_AD_BLOCKER_WARNING_SHOWN:
      return { ...state, lastShownDate: action.date }

    default:
      return state;
  }
};

export default app;
